import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Menu,
  LogOut,
  X,
  User,
  Settings,
  Activity,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { roleConfigs } from "../config/roleConfigs";
import {
  isAdminConfig,
  type AccountSubMenuItem,
  type UserRole,
} from "../types/roles";
import { clearUserData } from "../utils/auth";
import GlobalSearch from "../components/common/GlobalSearch";

interface SidebarProps {
  role: UserRole;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  role,
  onLogout,
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
}) => {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const location = useLocation();
  const config = roleConfigs[role];

  const toggleAccountMenu = () => {
    setAccountMenuOpen(!accountMenuOpen);
  };

  const isActiveLink = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          ${isCollapsed ? "w-20" : "w-72"} 
          h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 
          text-white fixed top-0 left-0 flex flex-col transition-all duration-300 z-50
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          shadow-2xl border-r border-blue-700/50
        `}
      >
        <div className="flex justify-between items-center p-6 border-b border-blue-700/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
            {!isCollapsed && (
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-300 to-blue-100 bg-clip-text text-transparent">
                  MIMAP
                </h2>
                <p className="text-xs text-blue-300">Medical Platform</p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleCollapse}
              className="p-2 rounded-lg hover:bg-blue-700/50 transition-colors hidden lg:block"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-blue-300" />
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-blue-700/50 transition-colors lg:hidden"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-blue-300" />
            </button>
          </div>
        </div>

        <nav className="flex flex-col space-y-2 p-4 flex-grow">
          {config.menuItems.map((item) => {
            const isActive = isActiveLink(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => window.innerWidth < 1024 && onClose()}
                className={`
                  flex items-center gap-4 p-3 rounded-xl transition-all duration-200 group
                  ${
                    isActive
                      ? "bg-gradient-to-r from-blue-500/30 to-blue-600/30 text-white border border-blue-400/50 shadow-lg"
                      : "hover:bg-blue-700/50 text-blue-200 hover:text-white"
                  }
                  ${isCollapsed ? "justify-center" : ""}
                `}
              >
                <item.icon
                  className={`w-5 h-5 ${
                    isActive ? "text-blue-300" : "group-hover:text-blue-300"
                  } transition-colors`}
                />
                {!isCollapsed && (
                  <span className="font-medium">{item.label}</span>
                )}
                {isActive && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-300 rounded-full"></div>
                )}
              </Link>
            );
          })}

          {isAdminConfig(config) && config.accountSubMenu && (
            <div className="flex flex-col">
              <button
                onClick={toggleAccountMenu}
                className={`
                  flex items-center gap-4 p-3 rounded-xl transition-all duration-200 group w-full text-left
                  hover:bg-blue-700/50 text-blue-200 hover:text-white
                  ${isCollapsed ? "justify-center" : "justify-between"}
                `}
              >
                <div className="flex items-center gap-4">
                  <User className="w-5 h-5 group-hover:text-blue-300 transition-colors" />
                  {!isCollapsed && (
                    <span className="font-medium">Gestion des Comptes</span>
                  )}
                </div>
                {!isCollapsed && (
                  <div className="transition-transform duration-200">
                    {accountMenuOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                )}
              </button>

              {accountMenuOpen && !isCollapsed && (
                <div className="ml-6 mt-2 flex flex-col space-y-1 border-l border-blue-700/50 pl-4">
                  {config.accountSubMenu.map((item: AccountSubMenuItem) => {
                    const isActive = isActiveLink(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => window.innerWidth < 1024 && onClose()}
                        className={`
                          flex items-center gap-3 p-2 rounded-lg transition-all duration-200
                          ${
                            isActive
                              ? "bg-blue-500/30 text-blue-200 border-l-2 border-blue-300"
                              : "hover:bg-blue-700/30 text-blue-300 hover:text-white hover:border-l-2 hover:border-blue-400"
                          }
                        `}
                      >
                        <span className="text-sm font-medium">
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-blue-700/50">
          <button
            onClick={onLogout}
            className={`
              w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-200 group
              hover:bg-red-500/20 text-blue-200 hover:text-red-300 border border-transparent hover:border-red-500/30
              ${isCollapsed ? "justify-center" : ""}
            `}
          >
            <LogOut className="w-5 h-5" />
            {!isCollapsed && <span className="font-medium">Déconnexion</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

interface HeaderProps {
  role: UserRole;
  onToggleSidebar: () => void;
  isCollapsed: boolean;
}

const Header = React.memo(
  ({ role, onToggleSidebar, isCollapsed }: HeaderProps) => {
    const normalizeRole = (roleString: string): keyof typeof roleConfigs => {
      const normalized = roleString.toLowerCase();
      if (normalized === "admin" || normalized === "administrator")
        return "admin";
      if (
        normalized === "doctor" ||
        normalized === "médecin" ||
        normalized === "medecin"
      )
        return "doctor";
      return "patient";
    };

    const normalizedRole = normalizeRole(role);
    const config = roleConfigs[normalizedRole];

    const [userLabel, setUserLabel] = useState(() => {
      const storedLabel = localStorage.getItem("userLabel");
      return storedLabel && storedLabel !== "Utilisateur Inconnu"
        ? storedLabel
        : config?.userLabel || "Utilisateur";
    });

    const [userEmail, setUserEmail] = useState(() => {
      const storedEmail = localStorage.getItem("userEmail");
      return storedEmail && storedEmail !== "email@inconnu.com"
        ? storedEmail
        : config?.userEmail || "user@mimap.com";
    });

    useEffect(() => {
      const updateUserInfo = () => {
        const storedLabel = localStorage.getItem("userLabel");
        const storedEmail = localStorage.getItem("userEmail");
        setUserLabel(
          storedLabel && storedLabel !== "Utilisateur Inconnu"
            ? storedLabel
            : config?.userLabel || "Utilisateur"
        );
        setUserEmail(
          storedEmail && storedEmail !== "email@inconnu.com"
            ? storedEmail
            : config?.userEmail || "user@mimap.com"
        );
      };

      updateUserInfo();
      window.addEventListener("storage", updateUserInfo);
      return () => window.removeEventListener("storage", updateUserInfo);
    }, [config]);

    return (
      <header
        className={`
        h-16 bg-white/80 backdrop-blur-lg shadow-sm border-b border-slate-200/50
        flex items-center justify-between px-6 fixed top-0 w-full z-30
        transition-all duration-300
        ${isCollapsed ? "lg:pl-28" : "lg:pl-80"}
      `}
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors lg:hidden"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-slate-600" />
          </button>
          <h1 className="text-xl font-semibold text-slate-800">
            {config?.title || "Dashboard"}
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          <GlobalSearch />
          <button className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <Settings className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium text-slate-700">{userLabel}</p>
              <p className="text-xs text-slate-500">{userEmail}</p>
            </div>
          </div>
        </div>
      </header>
    );
  }
);

interface DashboardLayoutProps {
  role: UserRole;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ role }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const config = roleConfigs[role];

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    clearUserData();
    navigate(config.loginRoute);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Sidebar
        role={role}
        onLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />
      <Header
        role={role}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        isCollapsed={isCollapsed}
      />
      <main
        className={`
          pt-20 p-6 min-h-screen transition-all duration-300
          ${isCollapsed ? "lg:pl-28" : "lg:pl-80"}
        `}
      >
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
